import React, { useEffect, useState } from "react";
import Spacer from "components/global/Spacer";
import { connect } from "react-redux";
import styled from "styled-components";
import Backdrop from "components/global/Backdrop";
import Button from "components/global/Button";
import DropdownCustom from "components/global/DropdownCustom";
import ModalWrapper from "components/global/ModalWrapper";
import { axiosInstance, buyAiCredit, getAccount, trackEvents } from "api";
import { showAlert } from "utils/utils";
import BillingCardSelect from "views/billing/BillingCardSelect";

const Wrapper = styled(Backdrop)`
  .card {
    width: 480px;
    padding: 48px;
    background-color: var(--white);
    border-radius: 16px;

    .title {
      font-size: 18px;
      font-style: normal;
      font-weight: 700;
      line-height: 20px;
    }

    .description {
      font-size: 14px;
      font-style: normal;
      line-height: 18px;
      font-weight: 450;
    }

    label {
      font-size: 14px;
      font-style: normal;
      font-weight: 450;
    }

    .brandBox {
      border-radius: 8px;
      display: flex;
      align-items: center;
      padding: 12px;
      width: 100%;
      border: 1px solid var(--secondary);
      height: 52px;
      background: #ebf6ff;
    }

    .amount {
    }
  }

  .btn {
    height: 48px;
    width: calc(50% - 12px);
    border-radius: 8px;
    padding: 0 24px;
    text-transform: capitalize;

    &.cancel {
      color: var(--body_text);
      border: 1px solid var(--grey_10);
    }

    &.actionBtn {
      color: var(--white);
      background-color: var(--secondary);
    }
  }

  @media screen and (max-width: 768px) {
    .card {
      width: calc(100% - 24px);
      padding: 24px;

      .title {
        font-size: 16px;
        line-height: 20px;
      }

      .description {
        font-size: 14px;
        line-height: 20px;
      }

      .textDescription {
        font-weight: 450;
      }

      .pricePer {
        font-size: 14px;
        font-style: normal;
        font-weight: 500;
      }
    }

    .actionBtns {
      button {
        font-size: 14px;
        font-style: normal;
        font-weight: 450;
      }
    }
  }
`;

const AiCreditIncrement = ({ open, close, current_sub, active_account }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [prices, setPrices] = useState([]);
  const [credit, setCredit] = useState("");
  const [showBillingCardSelect, setShowBillingCardSelect] = useState(false);
  const [selectedCard, setSelectedCard] = useState(null);

  useEffect(() => {
    const getPricing = async () => {
      try {
        const res = await axiosInstance.get("/ai-credit-pricing");
        if (res?.data?.status === true) {
          setPrices(res?.data?.data?.ai_credit_pricing);
          setCredit(
            `${res?.data?.data?.ai_credit_pricing[0]?.amount} AI credits`
          );
        }
      } catch (error) {
        console.log(error);
      }
    };
    getPricing();
  }, []);

  const price = prices?.find((price) =>
    credit?.includes(price?.amount?.toString())
  );

  const trackPurchaseCredit = async () => {
    const data = {
      event_name: "purchase_ai_credit",
      event_properties: {
        date: new Date(),
        user_type: active_account?.industry,
        account_type: current_sub?.plan_name,
        ai_credit_pack_type: price.amount,
        ai_credit_cost:
          current_sub?.plan.currency === "NGN"
            ? price?.ngn_price
            : price?.usd_price,
      },
    };
    try {
      await trackEvents(data);
    } catch (error) {
      console.log(error);
    }
  };

  const handleAddPaymentMethod = async () => {
    setIsLoading(true);
    try {
      const res = await buyAiCredit({ credit: price.id });
      if (res.data.status === true) {
        await trackPurchaseCredit();
        const redirectUrl =
          res?.data?.data?.redirect?.url ?? res?.data?.data?.redirect;
        if (redirectUrl) {
          window.location.replace(redirectUrl);
          return;
        }
      } else if (res.data.status === false) {
        showAlert({
          msg: res.data.message,
          type: "danger",
        });
      }
      setIsLoading(false);
      close();
    } catch (error) {
      setIsLoading(false);
      showAlert({
        msg: error.response?.data?.message ?? "Something went wrong.",
        type: "danger",
      });
    }
  };

  const handleSubmit = async () => {
    setIsLoading(true);
    try {
      const res = await buyAiCredit({ credit: price.id }, selectedCard?.id);
      if (res.data.status === true) {
        await trackPurchaseCredit();
        const redirectUrl = res?.data?.data?.redirect;
        if (redirectUrl) {
          window.location.replace(redirectUrl);
          return;
        }

        if (!redirectUrl) {
          await getAccount();
          showAlert({
            msg: res.data?.message,
            type: "success",
          });
        }
      } else if (res.data.status === false) {
        showAlert({
          msg: res.data.message,
          type: "danger",
        });
      }
      setIsLoading(false);
      close();
    } catch (error) {
      setIsLoading(false);
      showAlert({
        msg: error.response?.data?.message ?? "Something went wrong.",
        type: "danger",
      });
    }
  };

  if (showBillingCardSelect) {
    return (
      <BillingCardSelect
        selectedCard={selectedCard}
        setSelectedCard={setSelectedCard}
        handlePayment={handleSubmit}
        showModal={showBillingCardSelect}
        closeModal={() => close()}
        submitting={isLoading}
        handleAddPaymentMethod={handleAddPaymentMethod}
      />
    );
  }

  return (
    <>
      <Wrapper className={`flexRow justifyEnd ${open && "show"}`}>
        <ModalWrapper>
          <div className="card flexColumn alignCenter">
            <h5 className="colorGrey1 textCenter title">
              {active_account?.total_ai_credits < 1
                ? "Get more with Paddy AI"
                : "Purchase AI credits"}
            </h5>
            <Spacer y={0.6} />
            <p className="l2 colorBodyText textCenter description">
              {active_account?.total_ai_credits < 1
                ? "You have used up all the AI credits in this workspace. Purchase more credits to continue using the AI Assistant."
                : " Get more creative ideas by topping your AI credits"}
            </p>
            <Spacer y={2.4} />
            <DropdownCustom
              name="credit"
              label="AI credit pack"
              list={prices?.map((item) => `${item.amount} AI credits`)}
              className="spanFull"
              value={credit}
              setValue={(value) => setCredit(value)}
              required={false}
            />
            <Spacer y={2.4} />
            <div className="brandBox amount flexRow justifySpaceBetween">
              <div className="flexRow alignCenter justifyCenter">
                <p className="l3 bold">Total</p>
              </div>
              <div className="flexRow alignCenter justifyCenter">
                <p className="l2 num">
                  <b>
                    {current_sub?.plan.currency === "NGN" ? "₦" : "$"}{" "}
                    {current_sub?.plan.currency === "NGN"
                      ? price?.ngn_price
                      : price?.usd_price}
                  </b>
                </p>
              </div>
            </div>
            <Spacer y={2.4} />
            <div className="flexRow justifySpaceBetween fullWidth actionBtns">
              <Button
                text="Cancel"
                onClick={close}
                fullWidth
                bg="#fff"
                color="var(--grey_1)"
                bd="var(--grey_10)"
                disabled={isLoading}
              />
              <Spacer x={2.4} />
              <Button
                text="Purchase"
                disabled={isLoading || !prices.length}
                loading={isLoading}
                onClick={() => {
                  if (current_sub?.plan?.currency === "NGN") {
                    setShowBillingCardSelect(true);
                  } else {
                    handleSubmit();
                  }
                }}
                bg="var(--secondary)"
                fullWidth
              />
            </div>
          </div>
        </ModalWrapper>
      </Wrapper>
    </>
  );
};

const mapStateToProps = (state) => {
  return {
    current_sub: state.current_sub,
    active_account: state.active_account,
  };
};

export default connect(mapStateToProps)(AiCreditIncrement);
